import { useMemo } from "react";
import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
} from "@b2bportal/core-hotels";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { AmenityStatus } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { AmenityIcon } from "@hopper-b2b/ui";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import DOMPurify from "dompurify";
import { useApacHotelsStyles } from "../../hooks";
import { ApacHotelsComponent } from "../../types";
import defaultStyles from "./styles.module.scss";

export type DescriptionDialogProps = {
  description: string;
  amenities: Array<AmenityStatus>;
  showTriggerIcon?: boolean;
  title?: string;
  closeDialogText?: string;
};

export const DescriptionDialog = ({
  description,
  amenities,
  showTriggerIcon = false,
  title,
  closeDialogText,
}: DescriptionDialogProps) => {
  const COMPONENT_KEY = ApacHotelsComponent.DescriptionDialog;
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const htmlDescription = useMemo(
    () => DOMPurify.sanitize(description),
    [description]
  );

  const emitOpenDetailsEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelShopPageRoomsDetailsOpened
  );

  return (
    <Dialog>
      <DialogTrigger onClick={() => emitOpenDetailsEvent()}>
        <div className={cn("view-details")}>
          {showTriggerIcon && <Icon iconName={IconNameEnum.info} />}
          <span>{t("viewRoomAmenities")}</span>
        </div>
      </DialogTrigger>
      <DialogContent
        title={title}
        bottomFixedContent={
          <div className={cn("footer")}>
            <DialogClose className={cn("close-button")}>
              {closeDialogText ?? t("cancel")}
            </DialogClose>
          </div>
        }
      >
        <div className={cn("content")}>
          <div className={cn("amenities-list")}>
            {amenities.map((a) => (
              <span className={cn("amenity-item")}>
                <AmenityIcon
                  className={cn("amenity-icon")}
                  key={a.amenity}
                  amenity={a.amenity}
                  alt={a.displayName}
                />
                <span className={cn("amenity-text")}>{a.displayName}</span>
              </span>
            ))}
          </div>

          <div
            className={cn("amenities-description")}
            dangerouslySetInnerHTML={{
              __html: htmlDescription,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
